<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import Table from "../../../base-components/Table";
import Preview from "../../../base-components/Preview";
import { FormInput } from "../../../base-components/Form";
import Button from "../../../base-components/Button";
import LoadingIcon from "../../../base-components/LoadingIcon";
import Lucide from "../../../base-components/Lucide/Lucide.vue";
import { usePedidosStore } from "../../../services/pedidos";
import ModalPedidosLiveVer from "./modals/ModalPedidosLiveVer.vue";
import { userUserStore } from "../../../stores/user";
import { Pedido, pedidoItemConverter } from "@fixeduy/restaurantestypes";
// import { devolverHoraTimestamp } from "../../../utils/fecha";
import { collection, getFirestore, orderBy, query } from "firebase/firestore";
import { useCollection } from "vuefire";
import ModalPedidosLiveModificarEstado from "./modals/ModalPedidosLiveModificarEstado.vue";
import ModalPedidosLiveModificarEstadoItem from "./modals/ModalPedidosLiveModificarEstadoItem.vue";
import { getEstadoPedido } from "../../../constants/estadosPedido";
import { getEstadoPedidoItemLabel } from "@fixeduy/restaurantestypes";
import { PedidoItem } from "@fixeduy/restaurantestypes";
import { utilFechaConvertirNumeroString } from "../../../utils/fechaFixedTypes";

interface Props {
  pedido: Pedido;
}
const props = defineProps<Props>();

const emit = defineEmits(["cerrar", "pedido-editado"]);

const mostrarItems = ref(false);

enum showModalType {
  none,
  Ver,
  ModificarEstado
}

const modal = ref<showModalType>(showModalType.none);
const pedidoSeleccionado = ref<Pedido | null>(null);
const itemSeleccionado = ref<PedidoItem | null>(null);

const showModal = (pedido: Pedido, modalShow: showModalType) => {
  modal.value = modalShow;
  pedidoSeleccionado.value = pedido;
};

const showModalEstadoItem = (item: PedidoItem) => {
  itemSeleccionado.value = item;
};

const cerrarModal = () => {
  modal.value = showModalType.none;
  pedidoSeleccionado.value = null;
};

const cerrarModalItem = () => {
  itemSeleccionado.value = null;
};

const userStore = userUserStore();
const db = getFirestore();

const itemsRef = computed(() => {
  const empresaId = userStore.empresa?.id;
  const pedidoId = props.pedido?.id;
  if (!empresaId || !pedidoId) return null;

  return query(
    collection(
      db,
      `empresas/${empresaId}/pedidos/${pedidoId}/pedidos-items`
    ).withConverter(pedidoItemConverter),
    orderBy("fechaCreacion", "desc")
  );
});

const items = useCollection(itemsRef);

const itemsCount = computed(() => items.value?.length ?? 0);

const estadoPedido = computed(() => getEstadoPedido(props.pedido.estado));
</script>

<template>
  <Preview class="box">
    <div class="m-2">
      <div class="relative mb-5 rounded-xl px-4 py-3 border">
        <div class="flex items-center justify-between w-full">
          <!-- Izquierda -->
          <div class="flex items-center gap-3">
            <span class="text-sm font-semibold tracking-wide">
              #{{ props.pedido.numero }}
            </span>

            <div class="h-5 w-px bg-black/10 dark:bg-white/10"></div>
          </div>

          <!-- Centro -->
          <div class="flex justify-center">
            <span
              v-if="estadoPedido"
              class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border"
              :class="estadoPedido.badgeClasses"
            >
              {{ estadoPedido.label }}
            </span>
            <span
              v-else
              class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-slate-100 text-slate-600 border border-slate-200"
            >
              {{ pedido.estado }}
            </span>
          </div>

          <!-- Derecha -->
          <button
            class="flex items-center gap-2 text-sm font-medium hover:underline"
            @click="showModal(props.pedido, showModalType.Ver)"
          >
            <Lucide icon="Edit" class="w-4 h-4" />
            <span class="hidden sm:inline">Detalles</span>
          </button>
        </div>
      </div>
      <div class="flex items-center">
        <Lucide icon="ClockIcon" class="w-4 h-4 mr-2 text-slate-500" />
        Hora creación:
        <a class="ml-1">
          {{ utilFechaConvertirNumeroString(pedido.fechaCreacion, true, "/") }}
        </a>
      </div>
      <div class="flex">
        <div class="flex items-center mt-3">
          <Lucide icon="MapPinIcon" class="w-4 h-4 mr-2 text-slate-500" />
          <a class="ml-1">
            {{ pedido.mesaNombre }}</a
          >
        </div>
        <div class="flex items-center mt-3 ml-5">
          <Lucide icon="User" class="w-4 h-4 mr-2 text-slate-500" />
          {{ pedido.mozoId ?? "Sin asignar" }}
        </div>
      </div>

      <!-- <div class="flex items-center mt-3">
        <Lucide icon="BarChartIcon" class="w-4 h-4 mr-2 text-slate-500" />
        Estado:
        <span class="px-2 ml-1 rounded bg-success/20 text-success">
          {{ pedido.estado }}
        </span>
      </div> -->

      <div
        class="flex items-center mt-3 cursor-pointer"
        @click="mostrarItems = !mostrarItems"
      >
        <Lucide icon="List" class="w-4 h-4 mr-2 text-slate-500" />
        Items:
        <span class="ml-2 px-2 rounded bg-primary/10 text-primary">
          {{ itemsCount }}
        </span>
      </div>

      <!-- <div v-if="mostrarItems">
        <div v-for="item in items">
          {{ item.nombre }}
        </div>
      </div> -->

      <div
        v-if="mostrarItems"
        class="mt-3 rounded-xl border border-slate-200/60 dark:border-darkmode-400 overflow-hidden bg-white/60 dark:bg-transparent"
      >
        <ul class="divide-y divide-slate-200/60 dark:divide-darkmode-400">
          <li
            v-for="item in items"
            :key="item.id"
            class="p-4 flex gap-3 hover:bg-slate-50/70 dark:hover:bg-white/5 transition cursor-pointer"
            @click="showModalEstadoItem(item)"
          >
            <!-- Cantidad -->
            <div
              class="shrink-0 min-w-[34px] h-8 px-2 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold"
            >
              {{ item.cantidad ?? 1 }}
            </div>

            <!-- Contenido -->
            <div class="flex-1 min-w-0">
              <!-- Fila 1: Nombre + Estado -->
              <div class="flex flex-wrap items-start gap-2">
                <div class="font-medium leading-snug break-words">
                  {{ item.nombre }}
                </div>

                <span
                  class="ml-auto text-[11px] px-2 py-0.5 rounded-full border"
                  :class="{
                    'bg-emerald-50 text-emerald-700 border-emerald-200':
                      item.estado === 'listo' || item.estado === 'entregado',
                    'bg-amber-50 text-amber-700 border-amber-200':
                      item.estado === 'en_proceso' ||
                      item.estado === 'pendiente',
                    'bg-slate-100 text-slate-600 border-slate-200': !item.estado
                  }"
                >
                  {{ getEstadoPedidoItemLabel(item.estado ?? "") }}
                </span>
              </div>

              <!-- Fila 2: Observaciones -->
              <p
                v-if="item.observaciones"
                class="mt-1 text-sm text-slate-600 dark:text-slate-400 leading-snug break-words"
              >
                {{ item.observaciones }}
              </p>

              <!-- Fila 3: Meta -->
              <div
                v-if="item.fechaCreacion"
                class="mt-1 text-xs text-slate-400 flex items-center gap-2"
              >
                <Lucide icon="ClockIcon" class="w-3 h-3" />
                {{
                  utilFechaConvertirNumeroString(item.fechaCreacion, true, "/")
                }}
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div
        class="flex items-center pt-5 mt-5 font-medium border-t border-slate-200/60 dark:border-darkmode-400"
      >
        <Button
          variant="outline-secondary"
          type="button"
          class="w-full px-2 py-1"
          @click="showModal(props.pedido, showModalType.ModificarEstado)"
        >
          Cambiar estado
        </Button>
      </div>

      <ModalPedidosLiveVer
        v-if="modal == showModalType.Ver && pedidoSeleccionado != null"
        :pedido="pedidoSeleccionado!"
        @cerrar="cerrarModal"
      />
      <ModalPedidosLiveModificarEstado
        v-if="
          modal == showModalType.ModificarEstado && pedidoSeleccionado != null
        "
        :pedido="pedidoSeleccionado!"
        @cerrar="cerrarModal"
      />
      <ModalPedidosLiveModificarEstadoItem
        v-if="itemSeleccionado != null"
        :pedido="pedido"
        :item="itemSeleccionado"
        @cerrar="cerrarModalItem"
      />
    </div>
  </Preview>
</template>

<style scoped>
.bloqueado {
  color: rgb(185 28 28);
  padding: 0 0 0 10px;
}
</style>
